import { inject, Injectable } from '@angular/core';
import { BreakpointService, PlatformService } from '@big-direkt/utils/shared';
import { type ScrollOptions } from '../models/scroll-options';
import { EnvironmentService } from './environment.service';
import { WindowService } from './window.service';

declare global {
    interface Document {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        documentMode?: any;
    }
}

@Injectable({
    providedIn: 'root',
})
export class ScrollService {
    public static readonly defaultSelector = 'h1';

    private readonly breakpointService = inject(BreakpointService);
    private readonly windowService = inject(WindowService);
    private readonly platformService = inject(PlatformService);
    private readonly environmentService = inject(EnvironmentService);

    private readonly defaultScrollOptions: ScrollOptions = {
        spaceToHeaderParam: undefined,
        focusElement: false,
        scrollIntoView: false,
        scrollIntoViewOptions: { behavior: 'smooth', block: 'center' },
    };
    private readonly defaultSpaceToHeader = 32;
    private isScrolling = false;

    public constructor() {
        this.windowService.nativeWindow()?.addEventListener('scrollend', () => {
            this.isScrolling = false;
        });
    }

    /**
     * Scrolls to the top of the page
     * @param behavior
     */
    public scrollTop(behavior: ScrollBehavior = 'smooth'): void {
        this.safeguardScrolling();
        this.windowService.nativeWindow()?.scrollTo({
            top: 0,
            left: 0,
            behavior,
        });
    }

    /**
     * Scrolls to the first found H1 dom element or to a given css selector target
     * @param {string | undefined} element the target to scroll
     * @param {ScrollOptions} scrollOptions additional scrolling options
     */
    public scroll(element?: string | HTMLElement, scrollOptions: ScrollOptions = this.defaultScrollOptions): void {
        const spaceToHeaderParam = scrollOptions.spaceToHeaderParam ?? this.defaultScrollOptions.spaceToHeaderParam;
        const focusElement = scrollOptions.focusElement ?? this.defaultScrollOptions.focusElement;

        const domElement: HTMLElement | null | undefined =
            typeof element === 'string' || !element
                ? this.windowService.document().querySelector<HTMLElement>(element ?? ScrollService.defaultSelector)
                : element;

        if (!domElement) {
            return;
        }

        // Attach to the next DOM Rendering cycle using requestAnimationFrame()
        this.windowService.nativeWindow()?.requestAnimationFrame((): void => {
            const bounding: DOMRect = domElement.getBoundingClientRect();
            const spaceToHeader = spaceToHeaderParam ?? this.defaultSpaceToHeader;
            const offset = this.breakpointService.topBarSize().height + this.breakpointService.headerSize().height + spaceToHeader;
            const additionalBottomOffsetMultiplier = 2;
            const viewportSize = this.breakpointService.viewPortSize();
            const scrollDestination: number = bounding.top - offset;

            if (scrollOptions.scrollIntoView) {
                /**
                 * Do not scroll when:
                 - element hight won't fit on screen
                 - if the bottom point of the element is already inside the viewport but not outside the top of the screen
                 */
                if (
                    viewportSize.height - offset * additionalBottomOffsetMultiplier <= bounding.height ||
                    (bounding.bottom >= 0 && bounding.bottom < viewportSize.height)
                ) {
                    return;
                }

                this.safeguardScrolling(domElement);
                domElement.scrollIntoView({ ...this.defaultScrollOptions.scrollIntoViewOptions, ...scrollOptions.scrollIntoViewOptions });

                return;
            }

            if (focusElement) {
                domElement.setAttribute('tabindex', '0');
                domElement.focus({ preventScroll: true });
                domElement.removeAttribute('tabindex');
            }

            this.safeguardScrolling(domElement);
            this.windowService.nativeWindow()?.scrollBy({
                top: scrollDestination,
                left: 0,
                behavior: 'smooth',
            });
        });
    }

    private safeguardScrolling(element?: HTMLElement): void {
        if (this.platformService.isPlatformServer()) {
            return;
        }

        if (this.isScrolling && !this.environmentService.production) {
            // eslint-disable-next-line no-console, @typescript-eslint/restrict-template-expressions
            console.warn('element fired scroll event while another has not finished', element);
        }

        this.isScrolling = true;
    }
}
